import Tooltip from "@/ui/Tooltip"
import type { Placement } from "@floating-ui/react"
import type { RefObject } from "react"
import { motion } from "motion/react"
import { useHudContext } from "../hud/shared/playbookHUDInfra"
import { METRIC_LABEL_BASELINE_TRIM_CLASS, useMetricHoverSwap } from "../hud/blocks/metrics/metricHoverSwap"
import { useMetricHoverTrigger } from "../hud/blocks/hoverSwapStickyZone"
import { useHoverSwapsDisabled } from "../hud/blocks/hoverSwapsDisabled"
import {
  MetricDefinitionPanel,
  useUnifiedHudPanels,
} from "../hud/blocks/panels/unifiedHoverPanels"
import { MetricTrigger } from "../hud/metric-details/MetricTrigger"
import { METRIC_DETAILS_HINT } from "../hud/metric-details/metricLabels"
import { TickerNumber } from "./TickerNumber"
import { SLOWDOWN_TIME_UNITS, formatSlowdownYears } from "./slowdownTimeFormat"

const SAFETY_TAX_BUTTON_CLASS_NAME = [
  "text-[10px] leading-none w-4 h-4 bg-black",
  "flex items-center justify-center rounded-[2px] text-white hover:opacity-100",
].join(" ")

const SAFETY_TAX_TOOLTIP = <div>
  <p className="mb-2 font-bold font-mono uppercase">Total Slowdown</p>
  <p>
    How much later AI R&amp;D milestones are reached than on an unconstrained
    development path, due to safety testing, red-teaming, and alignment
    requirements.
  </p>
  <p className="mt-2 border-t border-black/10 pt-1.5 font-sans text-[10px] text-[var(--race-background)]">{METRIC_DETAILS_HINT}</p>
</div>

export const SafetyTaxIndicator = ({
  interactive = false,
  boostMonths = 0,
  currentYear,
  onIncrement,
  onDecrement,
  oneLine = false,
  valueFirst = false,
  labelFontSize,
  label,
  tooltipPlacement = "top-start",
  tooltipPositionRef,
}: {
  interactive?: boolean
  boostMonths?: number
  currentYear?: number
  onIncrement?: () => void
  onDecrement?: () => void
  oneLine?: boolean
  valueFirst?: boolean
  labelFontSize?: string
  label?: string
  tooltipPlacement?: Placement
  tooltipPositionRef?: RefObject<HTMLElement | null>
}) => {
  const ctx = useHudContext()
  // Total slowdown in years: how much later AI R&D milestones are reached
  // than on the unconstrained (Plan D) path. Branch plans carry it as warp
  // lag, Plan A from the sheet's "Total slowdown (years)" row; the legacy
  // hudMetrics row (also years) is the fallback. phase.safetyTaxMultiplier
  // stays in the data as the alternative "Safety Tax" reading.
  const slowdownYears = ctx.currentPhase?.totalSlowdownYears
    ?? ctx.currentPhase?.hudMetrics?.slowdownAmount
    ?? 0
  const boostedYears = slowdownYears + boostMonths / 12
  const roundedMonths = Math.round(boostedYears * 12)
  const displayedMonths = Object.is(roundedMonths, -0) ? 0 : roundedMonths
  // Under a year the panel reads in months, from a year on in years — the
  // shared vocabulary in slowdownTimeFormat.ts (also the details panel's NOW
  // number); the pieces are computed here too because TickerNumber animates
  // value/suffix/format separately rather than taking a finished string.
  const showMonths = Math.abs(displayedMonths) < 12
  const displayedSuffix = showMonths
    ? SLOWDOWN_TIME_UNITS.months
    : Math.round(boostedYears * 10) === 10
      ? SLOWDOWN_TIME_UNITS.yearSingular
      : SLOWDOWN_TIME_UNITS.yearsPlural
  const buttonsVisible = currentYear === undefined || currentYear >= 2028
  // Inside a <MetricHoverSwap> provider the floating tooltip is suppressed:
  // hover is reported to the swap context instead, and a MetricHoverSwapTarget
  // elsewhere on the HUD renders this metric's definition in-canvas. The
  // MetricTrigger (click → details panel) stays either way.
  const metricSwap = useMetricHoverSwap()
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  // Red-chip treatment (tab 3): the stat row stretches its items so the four
  // hover chips share one height — the wrappers below stretch the trigger to
  // the row's full band instead of centering it at natural height.
  const chipRow = metricSwap?.triggerHighlight === "red-chip"
  const unifiedPanels = useUnifiedHudPanels()
  // The shared formatter renders the same string the TickerNumber below
  // animates piecewise.
  const metricHoverProps = useMetricHoverTrigger(
    "safetyTax",
    formatSlowdownYears(boostedYears),
  )
  const trigger = (
    <MetricTrigger
      id="safetyTax"
      // self-stretch/px-1.5 belong to the production stats row; the px-1.5
      // is its tooltip hover surface. The swap row goes flush (the padding
      // would indent the figure off the policy column's edge), and hover-less
      // surfaces (the mobile dashboard) drop the padding too — no tooltips
      // there, and its narrow stat columns need the width.
      // No w-full in the chip row: the chip's negative side margins make a
      // percentage width resolve narrower than the content, so the red plate
      // stops short and the white text runs on invisibly past it. Sized to
      // content, the chip wraps the figures exactly like the other three.
      className={valueFirst && !metricSwap
        ? `flex items-start self-stretch ${hoverSwapsDisabled ? "" : "px-1.5"}`
        : chipRow ? "flex items-stretch gap-2" : "flex items-center gap-2 w-full"}
      {...metricHoverProps}
    >
      {/* <HourglassIcon className="w-4 h-4" /> */}
      <div className={valueFirst
        ? "flex flex-col-reverse justify-end items-center w-min"
        : oneLine ? "flex w-full items-baseline justify-between gap-1.5" : "relative top-[1px]"}>
        <div className={`text-[11px] ${valueFirst ? "text-center" : "whitespace-nowrap"} ${valueFirst && metricSwap ? METRIC_LABEL_BASELINE_TRIM_CLASS : ""}`} style={labelFontSize ? { fontSize: labelFontSize } : undefined}>
          {label ?? "Total Slowdown"}
        </div>
        <div className={`text-[11px] font-bold whitespace-nowrap ${valueFirst ? "" : ""}`}>
          <TickerNumber
            value={showMonths ? displayedMonths : boostedYears}
            suffix={displayedSuffix}
            format={{ maximumFractionDigits: showMonths ? 0 : 1 }}
          />
        </div>
      </div>
    </MetricTrigger>
  )

  return (
    <div className={`flex ${valueFirst && !interactive && !metricSwap ? "items-stretch self-stretch" : chipRow ? "items-stretch" : "items-center"}`}>
      {interactive && (
        <motion.div
          initial={false}
          animate={{
            width: buttonsVisible ? "auto" : 0,
            opacity: buttonsVisible ? 1 : 0,
          }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className={`overflow-hidden flex flex-col items-center gap-1 ${buttonsVisible ? "mr-1.5" : ""}`}
        >
          <Tooltip
            placement="top-start"
            maxWidth={150}
            content={<div className="p-2 text-[10px]">
              Simulate increasing safety tax by 1 month.
            </div>}
          >
            <button
              type="button"
              onClick={onIncrement}
              className={SAFETY_TAX_BUTTON_CLASS_NAME}
              aria-label="Increase safety tax"
            >
              +
            </button>
          </Tooltip>
          <Tooltip
            placement="top-start"
            maxWidth={150}
            content={<div className="p-2 text-[10px]">
              Simulate decreasing safety tax by 12 months.
            </div>}
          >
            <button
              type="button"
              onClick={onDecrement}
              className={SAFETY_TAX_BUTTON_CLASS_NAME}
              aria-label="Decrease safety tax"
            >
              −
            </button>
          </Tooltip>
        </motion.div>
      )}
      {/* Hover-less surfaces (the mobile dashboard): no definition tooltip —
          a tap must go straight to the MetricTrigger's click (the details
          page) instead of surfacing the hover affordance. */}
      {metricSwap || hoverSwapsDisabled ? trigger : (
        <Tooltip
          placement={tooltipPlacement}
          positionRef={tooltipPositionRef}
          disableShadow={Boolean(unifiedPanels)}
          content={unifiedPanels ? (
            <MetricDefinitionPanel metricId="safetyTax" />
          ) : (
            <div className="p-2 w-[280px]">{SAFETY_TAX_TOOLTIP}</div>
          )}
        >
          {trigger}
        </Tooltip>
      )}
    </div>
  )
}
